<template>
    <div>
        <h2>Configure G2 Link Devices</h2>
        <div class="d-flex flex-column flex-sm-row">
            <div class="d-flex flex-column mb-2 ">
                <h3>Step 1: Build the configuration</h3>
                <span class="text-muted">To build a configuration, first enable a configuration group by checking the box next to the group name. Then, configure the settings for that group according to your preferences.</span>
            </div>
            <button class="btn btn-secondary btn-sm ml-auto mb-2 align-self-end" style="flex-shrink: 0" @click="toggleAllConfig">{{ all_toggle.config === false ? 'Select' : 'Deselect' }} all config groups</button>
        </div>
        <div class="row">
            <div class="col-6 col-md-4 col-lg-2">
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th>
                                <input style="width: 1.5rem" type="checkbox" v-model="selected_config.volume_input"/>
                                Volume Input
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.volume_input">
                            <td>
                                <div class="d-flex justify-content-between">
                                    <label :for="key" class="m-0">{{ label(key) }}</label>
                                    <input
                                        :id="key"
                                        type="number"
                                        min="0" max="100" step="5"
                                        v-model="config.volume_input[key]"
                                        @change="e => handleVolumeNumberChange(e, config)"
                                        :disabled="!selected_config.volume_input"
                                    />
                                </div>
                                <input
                                    :id="key"
                                    type="range"
                                    min="0" max="100" step="5"
                                    v-model="config.volume_input[key]"
                                    :disabled="!selected_config.volume_input"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-6 col-md-4 col-lg-2">
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th>
                                <input style="width: 1.5rem" type="checkbox" v-model="selected_config.volume_output"/>
                                Volume Output
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.volume_output">
                            <td>
                                <div class="d-flex justify-content-between">
                                    <label :for="key" class="m-0">{{ label(key) }}</label>
                                    <input
                                        :id="key"
                                        type="number"
                                        min="0" max="100" step="5"
                                        v-model="config.volume_output[key]"
                                        @change="(e) => handleVolumeNumberChange(e, config)"
                                        :disabled="!selected_config.volume_output"
                                    />
                                </div>
                                <input
                                    :id="key"
                                    type="range"
                                    min="0" max="100" step="5"
                                    v-model="config.volume_output[key]"
                                    :disabled="!selected_config.volume_output"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-12 col-md-4">
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">
                                <input style="width: 1.5rem" type="checkbox" v-model="selected_config.band"/>
                                Band
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.band">
                            <td>{{ label(key) }}</td>
                            <td>
                                <select
                                    v-if="key === 'band_preset'"
                                    :id="key"
                                    v-model="config.band[key]"
                                    :disabled="!selected_config.band"
                                >
                                    <option value="0">Custom</option>
                                    <option value="1">Flat</option>
                                    <option value="2">Full</option>
                                    <option value="3">Low</option>
                                    <option value="4">Mid</option>
                                    <option value="5">High</option>
                                </select>
                                <select
                                    v-else-if="key === 'band_enable'"
                                    :id="key"
                                    v-model="config.band[key]"
                                    :disabled="!selected_config.band"
                                >
                                    <option value="1">ON</option>
                                    <option value="0">OFF</option>
                                </select>
                                <div v-else class="d-flex">
                                    <input 
                                        :id="key"
                                        type="range"
                                        min="-5" max="5" step="1"
                                        v-model="config.band[key]"
                                        :disabled="!selected_config.band || config.band.band_preset !== '0'"
                                    />
                                    <label class="text-center w-25">
                                        {{ config.band[key] }}
                                    </label>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">
                                <input style="width: 1.5rem" type="checkbox" v-model="selected_config.network"/>
                                Network
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.network">
                            <td>{{ label(key) }}</td>
                            <td>
                                <select 
                                    v-model="config.network[key]"
                                    :disabled="!selected_config.network"
                                >
                                    <option value="1">ON</option>
                                    <option value="0">OFF</option>
                                </select>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-12 col-md-4 d-flex flex-column justify-content-between">
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">
                                <input style="width: 1.5rem" type="checkbox" v-model="selected_config.sip"/>
                                SIP
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.sip">
                            <td>{{ label(key) }}</td>
                            <td>
                                <select 
                                    v-if="key === 'sip_tts_ext1' || key === 'sip_tts_ext2'"
                                    :id="key"
                                    v-model="config.sip[key]"
                                    :disabled="!selected_config.sip"
                                >
                                    <option v-if="key === 'sip_tts_ext1'" value="EXT1">Calling Extension 1</option>
                                    <option v-else value="EXT2">Calling Extension 2</option>
                                    <option value="OFFICE">Calling Office</option>
                                    <option value="EMRGNCY">Calling Emergency</option>
                                    <option value="DISABLED">Disabled</option>
                                </select>
                                <select v-else-if="key === 'sip_autoanswer'"
                                    :id="key"
                                    v-model="config.sip[key]"
                                    :disabled="!selected_config.sip"
                                >
                                    <option value="1">ON</option>
                                    <option value="0">OFF</option>
                                </select>
                                <input v-else
                                    :id="key"
                                    v-model="config.sip[key]"
                                    :disabled="!selected_config.sip"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">
                                <input style="width: 1.5rem" type="checkbox" v-model="selected_config.general"/>
                                General
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.general">
                            <td>{{ label(key) }}</td>
                            <td>
                                <input
                                    :id="key"
                                    v-model="config.general[key]"
                                    :disabled="!selected_config.general"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-12 col-md-4">
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">
                                <input style="width: 1.5rem" type="checkbox" v-model="selected_config.keypad"/>
                                Keypad
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.keypad">
                            <td>{{ label(key) }}</td>
                            <td>
                                <select
                                    :id="key"
                                    v-model="config.keypad[key]"
                                    :disabled="!selected_config.keypad"
                                >
                                    <option value="EXT1">Call SIP Extension 1</option>
                                    <option value="EXT2">Call SIP Extension 2</option>
                                    <option value="ANSWER">Answer SIP</option>
                                    <option value="HANGUP">Hangup SIP</option>
                                    <option value="PGUP">Page Up</option>
                                    <option value="PGDWN">Page Down</option>
                                    <option value="DISABLED">Disabled</option>
                                </select>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-12 col-md-4">
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">
                                <input style="width: 1.5rem" type="checkbox" v-model="selected_config.dns"/>
                                DNS
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.dns">
                            <td>{{ label(key) }}</td>
                            <td>
                                <input
                                    :id="key"
                                    v-model="config.dns[key]"
                                    :disabled="!selected_config.dns"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">
                                <input style="width: 1.5rem" type="checkbox" v-model="selected_config.bluetooth"/>
                                Bluetooth
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.bluetooth">
                            <td>{{ label(key) }}</td>
                            <td>
                                <select 
                                    v-if="key === 'bluetooth_use_pairing'"
                                    :id="key"
                                    v-model="config.bluetooth[key]"
                                    :disabled="!selected_config.bluetooth"
                                >
                                    <option value="true">ON</option>
                                    <option value="false">OFF</option>
                                </select>
                                <input
                                    v-else
                                    :id="key"
                                    v-model="config.bluetooth[key]"
                                    :disabled="!selected_config.bluetooth"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div> 
            <div class="col-12">
                <div class="d-flex flex-column">
                    <h3 style="flex-shrink: 0">Step 2: Select the devices</h3>
                    <span class="text-muted mb-2">To choose the devices you want to configure, simply check the box next to the device name in the list below. Once you've made your selections, click 'Configure Selected Devices' to begin the configuration process.</span>
                </div>
                <table class="table table-sm table-bordered col-12 col-lg-9 col-xl-6">
                    <thead class="thead-light">
                        <tr>
                            <th style="width: 6rem;">
                                <button class="btn btn-sm p-0" @click="toggleAllDevices">{{ all_toggle.devices === false ? 'Select' : 'Deselect' }} All</button>
                            </th>
                            <th>Name</th>
                            <th>IP Address</th>
                            <th style="width: 8rem">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="device in link_devices">
                            <td>
                                <input type="checkbox" v-model="selected_devices[device.device_id]"/>
                            </td>
                            <td>
                                {{ device.name }}
                            </td>
                            <td>
                                {{ device.ip }}
                            </td>
                            <td>
                                <StatusIndicator :status="config_status[device.device_id]"/>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div class="d-flex justify-content-start">
            <button type="button" class="btn btn-secondary mr-4" @click="reset" :disabled="is_configuring">Reset</button>
            <button type="button" class="btn btn-hub-primary" @click="submit" :disabled="is_configuring">{{ is_configuring ? 'Configuring...' : 'Configure Selected Devices'}}</button>
        </div> 
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import WebSocketConnection from '../../../assets/js/socket';
import { initialConfig as config, configLabelMap } from '../../../assets/js/g2link-config';
import { DEVICE_TYPE } from '../../../assets/js/resource-types';
import StatusIndicator from './StatusIndicator.vue';

const initialConfig = () => {
    const initial_config = JSON.parse(JSON.stringify(config));
    delete initial_config.network.ethernet_ip_mode;
    delete initial_config.network.ethernet_ip;
    delete initial_config.network.ethernet_gateway;
    delete initial_config.network.ethernet_netmask;
    delete initial_config.network.ethernet_mac;
    delete initial_config.general.machine_name;
    delete initial_config.bluetooth.bluetooth_name;
    delete initial_config.sip.sip_auth_name;
    delete initial_config.sip.sip_auth_pass;
    return initial_config;
}

const handleVolumeNumberChange = (e, config) => {
    if(e.target.value === '' || Number(e.target.value) < 0) {
        e.target.value = 0
        e.target.id.includes('input') ? config.volume_input[e.target.id] = 0 : config.volume_output[e.target.id] = 0;
    } else if (Number(e.target.value) >= 100) {
        e.target.value = 100
        e.target.id.includes('input') ? config.volume_input[e.target.id] = 100 : config.volume_output[e.target.id] = 100;
    } else {
        e.target.value = Number(e.target.value);
        e.target.id.includes('input') ? config.volume_input[e.target.id] = e.target.value : config.volume_output[e.target.id] = e.target.value;
    }
}

export default {
    components: { StatusIndicator },
    data() {
        return {
            socket: null,
            config: initialConfig(),
            auth: '0d7f9280e80fd038658dc8396cdf3ce6',
            selected_devices: {},
            selected_config: {},
            config_status: {},
            config_queue: [],
            is_configuring: false,
            all_toggle: {
                config: false,
                devices: false
            }
        }
    },
    watch: {
        link_devices(devices) {
            devices.forEach(device => {
                this.$set(this.selected_devices, device.device_id, false);
                this.$set(this.config_status, device.device_id, 'not-started');
            });
        }
    },
    computed: {
        ...mapGetters({
            devices: 'allDevices'
        }),
        link_devices() {
            return this.devices.filter(device => device.device_type_id === DEVICE_TYPE.G2Link).sort((a, b) => Number(a.device_id) - Number(b.device_id));
        }
    },
    methods: {
        handleVolumeNumberChange,
        openSocketConnection(device, openCallback, errorCallback) {
            if(!this.socket) {
                this.socket = new WebSocketConnection(`ws://${device.ip}/websocket`, 'interlock-protocol', { maxReconnections: 1 });
                this.socket.on('message', this.receiveSocketMessage);
                this.socket.on('open', openCallback);
                this.socket.on('error', errorCallback);
                this.socket.on('close', () => this.socket = null);
                this.socket.connect();
            }
        },
        closeSocketConnection() {
            if(this.socket) {
                this.socket.disconnect();
            }
        },
        sendSocketMessage(message) {
            if(this.socket) {
                this.socket.send({ ...message, authentication_token: this.auth });
            }
        },
        receiveSocketMessage(message) {
            const operation = message.operation;
            const from = message.from;
            if(operation === 'message' && from === 'write') {
                const value = message.value;
                if(value === 'success') {
                    this.verifyCount--;
                }
            }
        },
        toggleAllDevices() {
            this.all_toggle.devices = !this.all_toggle.devices;
            for(const id in this.selected_devices) {
                this.selected_devices[id] = this.all_toggle.devices;
            }
        },
        toggleAllConfig() {
            this.all_toggle.config = !this.all_toggle.config;
            for(const id in this.selected_config) {
                this.selected_config[id] = this.all_toggle.config;
            }
        },
        queueDevice(device) {
            this.config_queue.unshift(device);
        },
        startConfiguration() {
            if(this.config_queue.length > 0) {
                this.configureNextDevice();
            }
        },
        setSingleConfig(name, value) {
            const message = {
                operation: 'write',
                name: name,
                value: value
            }
            this.sendSocketMessage(message);
        },
        configureNextDevice() {
            if(this.config_queue.length === 0) {
                this.is_configuring = false;
                this.all_toggle.devices = false;
                return;
            }
            
            if(!this.socket) {
                const device = this.config_queue.pop();
                this.config_status[device.device_id] = 'connecting';
                this.openSocketConnection(device, () => {
                    this.verifyAttempt = 0;
                    this.config_status[device.device_id] = 'configuring';
                    this.configureConnectedDevice();
                    setTimeout(() => this.verifyConfiguration(device), 2000);
                }, () => {
                    this.config_status[device.device_id] = 'failure';
                    this.closeSocketConnection();
                    this.configureNextDevice();
                })
            } else {
                setTimeout(this.configureNextDevice, 100);
            }
        },
        configureConnectedDevice() {
            this.verifyCount = 0;
            for(const config in this.selected_config) {
                if(this.selected_config[config] !== true) continue;

                for(const key in this.config[config]) {
                    const name = key.replace(/_/g, '-');
                    this.verifyCount++;
                    this.setSingleConfig(name, this.config[config][key]);
                }
            }
        },
        verifyConfiguration(device) {
            this.verifyAttempt++;
            if(this.verifyCount === 0) {
                this.config_status[device.device_id] = 'success';
                this.selected_devices[device.device_id] = false;
                this.closeSocketConnection();
                this.configureNextDevice();
            } else if(this.verifyAttempt < 6) {
                this.configureConnectedDevice();
                setTimeout(() => this.verifyConfiguration(device), 2000 * this.verifyAttempt);
            } else {
                this.config_status[device.device_id] = 'failure';
                this.closeSocketConnection();
                this.configureNextDevice();
            }
        },
        submit() {
            if(!Object.values(this.selected_devices).includes(true) || !Object.values(this.selected_config).includes(true))
                return;

            Object.keys(this.config_status).forEach(key => {
                if(this.selected_devices[key] === true) 
                    this.config_status[key] = 'not-started'
            })
            this.is_configuring = true;
            for(const deviceId in this.selected_devices) {
                if(this.selected_devices[deviceId] === true) {
                    const device = this.devices.find(device => device.device_id === Number(deviceId));
                    this.queueDevice(device);
                }
            }
            this.startConfiguration();
        },
        reset() {
            this.config = initialConfig();
            this.all_toggle.config = false;
            this.all_toggle.devices = false;
            Object.keys(this.selected_devices).forEach(key => this.selected_devices[key] = false);
            Object.keys(this.selected_config).forEach(key => this.selected_config[key] = false);
            Object.keys(this.config_status).forEach(key => this.config_status[key] = 'not-started');
        },
        label(key) {
            return configLabelMap.get(key);
        }
    },
    created() {
        this.$store.dispatch('getDevices');
    },
    mounted() {
        for(const config in this.config) {
            this.$set(this.selected_config, config, false);
        }
    }
}
</script>

<style>
input, select {
    width: 100%;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number] {
  -moz-appearance: textfield;
  border: none;
  width: 2rem;
  height: 1.25rem;
}
</style>